export const COMPANY = {
  name: "DevOps Pro Cloud Inc",
  short: "DevOps Pro Cloud",
  phone: "+1 (318) 243-5224",
  phoneHref: "tel:+13182435224",
  email: "info@devopspro.cloud",
  emailHref: "mailto:info@devopspro.cloud",
  address: "King of Prussia, Pennsylvania, USA",
  tagline: "Engineer resilient cloud platforms. Ship secure software faster.",
};

export const NAV = [
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const SERVICES = [
  {
    slug: "devops",
    title: "DevOps Engineering",
    tag: "CI/CD · Automation",
    icon: "GitBranch",
    summary:
      "End-to-end pipelines, GitOps workflows, and self-healing platforms that ship 10× faster with fewer failures.",
    bullets: ["GitLab / GitHub / Jenkins pipelines", "Kubernetes-native delivery", "Blue-green & canary rollouts"],
  },
  {
    slug: "devsecops",
    title: "DevSecOps",
    tag: "Shift-left security",
    icon: "ShieldCheck",
    summary:
      "Bake security into every commit — SAST, DAST, SBOMs, policy-as-code, and continuous compliance for SOC2 / HIPAA / PCI.",
    bullets: ["Snyk / Trivy / Checkov guardrails", "OPA / Kyverno policy-as-code", "HIPAA & SOC2 hardening"],
  },
  {
    slug: "cloud-migration",
    title: "Cloud Migration",
    tag: "AWS · Azure · GCP",
    icon: "CloudUpload",
    summary:
      "Lift, refactor, or re-architect legacy stacks into multi-cloud landing zones with zero-downtime cutovers.",
    bullets: ["6R migration strategy", "Landing zones & multi-account guardrails", "Zero-downtime cutovers"],
  },
  {
    slug: "terraform",
    title: "Terraform + Terragrunt",
    tag: "IaC at scale",
    icon: "Boxes",
    summary:
      "Composable, DRY infrastructure using Terragrunt patterns — reusable modules, state safety, drift detection.",
    bullets: ["Modular IaC libraries", "Remote state + locking", "Atlantis / Spacelift pipelines"],
  },
  {
    slug: "ansible",
    title: "Ansible Automation",
    tag: "Config management",
    icon: "Cog",
    summary:
      "Idempotent playbooks and Ansible Automation Platform rollouts for fleet-wide config and patch management.",
    bullets: ["Playbook & role libraries", "AAP / AWX pipelines", "Fleet patching & CIS hardening"],
  },
  {
    slug: "splunk",
    title: "Splunk & Observability",
    tag: "SIEM · logs · metrics",
    icon: "Radar",
    summary:
      "Splunk Enterprise / Cloud setups, dashboards, and detections — plus Prometheus, Grafana, OpenTelemetry.",
    bullets: ["Splunk architecture & onboarding", "SIEM detections & SOAR", "OpenTelemetry rollout"],
  },
  {
    slug: "cloud-apps",
    title: "Cloud App Development",
    tag: "Cloud-native builds",
    icon: "Layers",
    summary:
      "Ship modern cloud-native apps — microservices, event-driven, container-first, API-first, production-ready.",
    bullets: ["Microservices & event buses", "Container-first delivery", "API gateways & mesh"],
  },
  {
    slug: "serverless",
    title: "Serverless",
    tag: "Lambda · Functions · Run",
    icon: "Zap",
    summary:
      "Cut ops overhead with Lambda, Azure Functions, Cloud Run, and event-driven architectures at any scale.",
    bullets: ["Event-driven design", "Step Functions / Durable / Workflows", "Cold-start & cost tuning"],
  },
  {
    slug: "finops",
    title: "FinOps & Cost Optimization",
    tag: "Cut cloud spend 30–60%",
    icon: "TrendingDown",
    summary:
      "Right-size, reserve, and rearchitect — measurable savings without sacrificing performance or reliability.",
    bullets: ["Rightsizing & Savings Plans", "Idle-resource elimination", "Chargeback & showback dashboards"],
  },
];

export const INDUSTRIES = [
  { title: "SaaS & Software", icon: "Code2", copy: "Multi-tenant platforms, CI/CD velocity, feature-flag rollouts, elastic scale for growth spikes." },
  { title: "Healthcare", icon: "HeartPulse", copy: "HIPAA-compliant landing zones, PHI-safe pipelines, audit-ready observability, HITRUST alignment." },
  { title: "Startups", icon: "Rocket", copy: "From MVP to Series-B — production-grade platforms without hiring an entire platform team." },
  { title: "Retail & E-commerce", icon: "ShoppingBag", copy: "Peak-day resilience, headless commerce, edge caching, and personalization pipelines that convert." },
];

export const PROCESS = [
  { step: "01", title: "Discover", copy: "Architecture audit, cost baseline, security posture review, and delivery-flow mapping." },
  { step: "02", title: "Design", copy: "Target-state blueprint — landing zones, pipelines, policies, and observability by design." },
  { step: "03", title: "Deploy", copy: "Iterative migration, IaC rollout, and pipeline enablement with zero-downtime cutovers." },
  { step: "04", title: "Optimize", copy: "Continuous FinOps, security drift monitoring, SRE, and quarterly platform reviews." },
];

export const CASE_STUDIES = [
  {
    industry: "Healthcare",
    title: "HIPAA-ready multi-account AWS landing zone",
    metric: "-42%",
    metricLabel: "monthly cloud spend",
    copy: "Rebuilt a national tele-health provider's AWS estate with Control Tower, Terragrunt guardrails and audit-ready Splunk pipelines.",
  },
  {
    industry: "Retail",
    title: "Peak-day autoscaling for a DTC brand",
    metric: "9.4×",
    metricLabel: "peak throughput",
    copy: "Migrated a monolithic Magento stack to a container-first stack on EKS with edge caching — Black Friday held with zero incidents.",
  },
  {
    industry: "SaaS",
    title: "GitLab GitOps pipeline for a Series-B SaaS",
    metric: "6.1×",
    metricLabel: "faster deploys",
    copy: "Rolled out GitLab CI + Argo CD across 42 microservices — from weekly deploys to on-demand, with policy-as-code guardrails.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "They rebuilt our AWS foundation from the ground up — HIPAA-ready, fully automated, and 40% cheaper. Best partner decision we've made.",
    author: "VP Engineering",
    org: "US Tele-Health Provider",
  },
  {
    quote:
      "The Terragrunt library and Splunk detections they shipped are still the backbone of our platform two years later. Rock solid.",
    author: "CTO",
    org: "Series-B FinTech",
  },
  {
    quote:
      "We went from 2 releases a week to on-demand deploys with policy-as-code enforcement. Their DevSecOps playbook is world-class.",
    author: "Director of Platform",
    org: "Global Retail Brand",
  },
];

export const STACK = [
  "AWS", "Azure", "Google Cloud", "Kubernetes", "Docker", "Terraform",
  "Terragrunt", "Ansible", "Splunk", "GitLab", "GitHub Actions", "ArgoCD",
  "Prometheus", "Grafana", "Helm", "Vault", "Consul", "OpenTelemetry",
];
